import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock, Copy, Loader2, Mail, RefreshCw, Save, Send, ShieldCheck } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { DEFAULT_VETO_HOURS, usePublishPolicy, type PolicyMode } from "@/components/content-simple/agentic";

type Tier = "platform" | "verified_sender" | "own_domain";
type Status = "ready" | "pending_verification" | "pending_dns" | "error";

interface DnsRecord { type: string; host: string; data: string; valid: boolean }
interface SenderFooter { legalName?: string; address?: string; unsubscribeText?: string }
interface SenderConfig {
  tier: Tier;
  fromName?: string;
  fromEmail?: string;
  replyTo?: string;
  platformLocalPart?: string;
  senderId?: number;
  domainId?: number;
  dns?: DnsRecord[];
  status: Status;
  error?: string;
  lastCheckedAt?: string;
  footer?: SenderFooter;
}
interface SenderView {
  config: SenderConfig;
  resolved: { from: { email: string; name: string }; replyTo?: string; tier: Tier; ready: boolean; reason?: string };
  platformAddress: string;
  platformDomain: string;
  ownerEmail: string | null;
  footerDefaults: { legalName: string; address: string };
}

/** apiRequest throws `Error("<status>: <body>")`; pull the server's `message` out of a JSON body when present. */
function serverMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : "";
  const body = raw.replace(/^\d+:\s*/, "");
  try {
    const parsed = JSON.parse(body) as { message?: unknown; error?: unknown };
    if (typeof parsed.message === "string") return parsed.message;
    if (typeof parsed.error === "string") return parsed.error;
  } catch { /* not JSON */ }
  return body || "Something went wrong. Please try again.";
}

const TIERS: { value: Tier; title: string; desc: string }[] = [
  { value: "platform", title: "Draper mail", desc: "Works now, no setup. Your email goes out from a Draper address with your name on it." },
  { value: "verified_sender", title: "Verify your address", desc: "We email you a link. Click it once and your emails go out from your own address." },
  { value: "own_domain", title: "Your own domain", desc: "Add 3 records at your registrar; we show them below. Your emails go out from your own domain." },
];

const STATUS_CHIP: Record<Status, { label: string; cls: string }> = {
  ready: { label: "Ready", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" },
  pending_verification: { label: "Waiting for your click", cls: "border-amber-500/30 bg-amber-500/10 text-amber-400" },
  pending_dns: { label: "Waiting for DNS", cls: "border-amber-500/30 bg-amber-500/10 text-amber-400" },
  error: { label: "Error", cls: "border-red-500/30 bg-red-500/10 text-red-400" },
};

const policyOptions = (hours: number): { value: PolicyMode; title: string; desc: string }[] => [
  { value: "manual", title: "Manual", desc: "You approve every email in the Lead Desk before it goes out." },
  { value: "veto", title: `${hours}-hour veto`, desc: `Rogers' emails to form leads go out ${hours} hours after they're written unless you discard them first.` },
  { value: "auto", title: "Automatic (answers inquiries; nurture needs consent)", desc: "Automatic: Rogers answers new form inquiries by itself (CASL inquiry basis) and continues the nurture sequence only for leads with express consent." },
];

function CopyButton({ value, label }: { value: string; label: string }) {
  const { toast } = useToast();
  return (
    <button
      type="button"
      aria-label={`Copy ${label}`}
      title={`Copy ${label}`}
      className="inline-flex items-center gap-1 rounded-md border border-border bg-background/40 px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          toast({ title: "Copied", description: `${label} is on your clipboard.` });
        } catch {
          toast({ title: "Couldn't copy", description: "Select the text and copy it by hand.", variant: "destructive" });
        }
      }}
      data-testid={`button-copy-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Copy size={11} /> Copy
    </button>
  );
}

const inputCls = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-blue-500/50 outline-none disabled:opacity-60";
const labelCls = "text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70";

/**
 * Settings -> Email. Who your email comes from (three tiers, one platform SendGrid account: you never need your own SendGrid or DNS),
 * the CASL footer every automated email carries, and how much of Rogers' email may go out without your click.
 * Owner / admin can change it; viewers see it read-only.
 */
export function EmailSenderSetting({ workspaceId, disabled, disabledReason }: { workspaceId: number; disabled?: boolean; disabledReason?: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const key = ["/api/workspaces", workspaceId, "email-sender"] as const;

  const { data, isLoading, isError } = useQuery<SenderView>({
    queryKey: key,
    enabled: !!workspaceId,
    // the app-wide default is "never refetch"; a sender waiting on a click or DNS can turn Ready while this page is closed (the tick re-checks it)
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${workspaceId}/email-sender`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });

  const saved = data?.config;
  const [tier, setTier] = useState<Tier>("platform");
  const [localPart, setLocalPart] = useState("");
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [legalName, setLegalName] = useState("");
  const [address, setAddress] = useState("");
  const [unsubText, setUnsubText] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // follow the server whenever it changes (first load, a save, a re-check, another tab)
  useEffect(() => {
    if (!saved) return;
    setTier(saved.tier);
    setLocalPart(saved.platformLocalPart ?? "");
    setFromName(saved.fromName ?? "");
    setFromEmail(saved.fromEmail ?? "");
    setReplyTo(saved.replyTo ?? "");
    setLegalName(saved.footer?.legalName ?? "");
    setAddress(saved.footer?.address ?? "");
    setUnsubText(saved.footer?.unsubscribeText ?? "");
    setFormError(null);
  }, [saved]);

  const dirty = useMemo(() => {
    if (!saved) return false;
    return tier !== saved.tier
      || localPart.trim() !== (saved.platformLocalPart ?? "")
      || fromName.trim() !== (saved.fromName ?? "")
      || (tier !== "platform" && fromEmail.trim().toLowerCase() !== (saved.fromEmail ?? ""))
      || replyTo.trim() !== (saved.replyTo ?? "")
      || legalName.trim() !== (saved.footer?.legalName ?? "")
      || address.trim() !== (saved.footer?.address ?? "")
      || unsubText.trim() !== (saved.footer?.unsubscribeText ?? "");
  }, [saved, tier, localPart, fromName, fromEmail, replyTo, legalName, address, unsubText]);

  const setView = (v: SenderView) => queryClient.setQueryData(key, v);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        tier,
        fromName: fromName.trim(),
        replyTo: replyTo.trim(),
        footer: { legalName: legalName.trim(), address: address.trim(), unsubscribeText: unsubText.trim() },
      };
      if (tier === "platform") body.platformLocalPart = localPart.trim().toLowerCase();
      else body.fromEmail = fromEmail.trim().toLowerCase();
      return (await apiRequest("PUT", `/api/workspaces/${workspaceId}/email-sender`, body)).json() as Promise<SenderView>;
    },
    onSuccess: (v) => {
      setView(v);
      setFormError(null);
      const t = v.config.tier;
      toast({
        title: "Email settings saved",
        description: t === "platform" ? "Your email goes out from Draper mail."
          : t === "verified_sender" ? (v.config.status === "ready" ? "Your address is verified." : `Check the inbox of ${v.config.fromEmail} for the link from SendGrid and click it.`)
          : (v.config.status === "ready" ? "Your domain is verified." : "Add the three DNS records below at your registrar, then press Check again."),
      });
    },
    onError: (e: unknown) => { const m = serverMessage(e); setFormError(m); toast({ title: "Couldn't save the email settings", description: m, variant: "destructive" }); },
  });

  const refresh = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/workspaces/${workspaceId}/email-sender/refresh`, {})).json() as Promise<SenderView>,
    onSuccess: (v) => {
      setView(v);
      toast(v.config.status === "ready"
        ? { title: "Ready", description: "SendGrid confirmed this sender." }
        : { title: STATUS_CHIP[v.config.status].label, description: v.config.error || "Not confirmed yet. Give it a few minutes and check again." });
    },
    onError: (e: unknown) => toast({ title: "Couldn't re-check", description: serverMessage(e), variant: "destructive" }),
  });

  const test = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/workspaces/${workspaceId}/email-sender/test`, {})).json() as Promise<{ to: string; from: string }>,
    onSuccess: (r) => toast({ title: "Test email sent", description: `Check ${r.to}. It was sent as ${r.from}.` }),
    onError: (e: unknown) => toast({ title: "The test email wasn't sent", description: serverMessage(e), variant: "destructive" }),
  });

  // ── Rogers sending policy (publish_policy.email) ──────────────────────────
  const { policy: publishPolicy, available: policyAvailable } = usePublishPolicy();
  const savedMode: PolicyMode = publishPolicy?.email?.mode ?? "manual";
  const [mode, setMode] = useState<PolicyMode>(savedMode);
  useEffect(() => { setMode(savedMode); }, [savedMode]);

  const savePolicy = useMutation({
    mutationFn: async () => (await apiRequest("PUT", `/api/workspaces/${workspaceId}/publish-policy`, {
      email: { mode, ...(mode === "veto" ? { vetoHours: publishPolicy?.email?.vetoHours ?? DEFAULT_VETO_HOURS } : {}), scope: "nurture_replies" },
    })).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspaceId, "publish-policy"] });
      toast({ title: "Sending policy saved", description: mode === "manual" ? "You approve every email." : "It applies to the emails Rogers writes from now on." });
    },
    onError: (e: unknown) => toast({ title: "Couldn't save the sending policy", description: serverMessage(e), variant: "destructive" }),
  });

  const locked = !!disabled;
  const busy = save.isPending || refresh.isPending || test.isPending;
  const chip = saved ? STATUS_CHIP[saved.status] : null;
  const isSavedTierReady = !!data?.resolved.ready;
  const footerComplete = !!saved?.footer?.legalName && !!saved?.footer?.address;
  const needsAttention: string[] = [];
  if (data && !data.resolved.ready) needsAttention.push(`your sender isn't ready yet${data.resolved.reason ? ` (${data.resolved.reason})` : ""}`);
  if (data && !footerComplete) needsAttention.push("your legal name and mailing address aren't set");

  return (
    <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-email-sender">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Mail size={20} className="text-blue-400" />
          <div>
            <h2 className="font-display font-bold text-foreground">Email</h2>
            <p className="text-xs text-muted-foreground/70 mt-0.5">Where Rogers' and Draper's email comes from. You never need your own SendGrid account or DNS.</p>
          </div>
        </div>
        {chip && (
          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${chip.cls}`} data-testid="chip-email-status" data-status={saved?.status}>
            {saved?.status === "ready" ? <CheckCircle2 size={12} /> : saved?.status === "error" ? <AlertTriangle size={12} /> : <Clock size={12} />}
            {chip.label}
          </span>
        )}
      </div>

      {isLoading && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={14} className="animate-spin" /> Loading your email settings…</div>}
      {!isLoading && (isError || !data) && <p className="text-xs text-red-300" data-testid="text-email-sender-error">Couldn't load your email settings. Reload the page to try again.</p>}

      {data && saved && (
        <>
          <p className="text-xs text-muted-foreground" data-testid="text-email-resolved">
            Right now your email goes out as <span className="font-medium text-foreground">{data.resolved.from.name} &lt;{data.resolved.from.email}&gt;</span>
            {data.resolved.replyTo ? <> and replies go to <span className="font-medium text-foreground">{data.resolved.replyTo}</span></> : null}.
            {!isSavedTierReady && data.resolved.reason ? <span className="text-amber-400"> Until it's ready, mail still goes out from Draper mail ({data.resolved.reason}).</span> : null}
          </p>
          {saved.status === "error" && saved.error && (
            <p className="flex items-start gap-1.5 text-xs text-red-300" data-testid="text-email-sender-saved-error"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{saved.error}</p>
          )}

          <fieldset className="space-y-2" disabled={locked || busy} data-testid="radiogroup-email-tier">
            <legend className="sr-only">Who your email comes from</legend>
            {TIERS.map((t) => {
              const checked = tier === t.value;
              return (
                <label
                  key={t.value}
                  className={`flex items-start gap-3 rounded-xl border p-3 transition-all ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${checked ? "border-blue-500/40 bg-blue-500/10" : "border-border bg-muted/20 hover:border-border/70"}`}
                  data-testid={`option-email-tier-${t.value}`}
                >
                  <input type="radio" name="email-tier" value={t.value} checked={checked} onChange={() => setTier(t.value)} className="mt-1 accent-blue-500" data-testid={`radio-email-tier-${t.value}`} />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-foreground">{t.title} <span className="font-normal text-muted-foreground">— {t.desc}</span></span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          <div className="grid gap-3 sm:grid-cols-2">
            {tier === "platform" ? (
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="email-local-part">Your Draper mail address</label>
                <div className="mt-1 flex items-center gap-1.5">
                  <input id="email-local-part" className={inputCls} value={localPart} onChange={(e) => setLocalPart(e.target.value)} disabled={locked || busy} placeholder="your-business" data-testid="input-email-local-part" />
                  <span className="shrink-0 text-sm text-muted-foreground">@{data.platformDomain}</span>
                </div>
              </div>
            ) : (
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="email-from-email">{tier === "verified_sender" ? "Send from this address" : "Send from an address on your domain"}</label>
                <input id="email-from-email" type="email" className={`${inputCls} mt-1`} value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} disabled={locked || busy}
                  placeholder={tier === "verified_sender" ? "you@yourcompany.com" : "hello@yourdomain.com"} data-testid="input-email-from-email" />
                <p className="mt-1 text-[11px] text-muted-foreground/70">
                  {tier === "verified_sender"
                    ? "SendGrid will email this address a confirmation link. It also needs your mailing address below."
                    : "We create the authentication for the domain after the @ and show you the records to add."}
                </p>
              </div>
            )}
            <div>
              <label className={labelCls} htmlFor="email-from-name">From name</label>
              <input id="email-from-name" className={`${inputCls} mt-1`} value={fromName} onChange={(e) => setFromName(e.target.value)} disabled={locked || busy} placeholder="Jane Smith via Acme" data-testid="input-email-from-name" />
            </div>
            <div>
              <label className={labelCls} htmlFor="email-reply-to">Replies go to</label>
              <input id="email-reply-to" type="email" className={`${inputCls} mt-1`} value={replyTo} onChange={(e) => setReplyTo(e.target.value)} disabled={locked || busy} placeholder={data.ownerEmail ?? "you@yourcompany.com"} data-testid="input-email-reply-to" />
            </div>
          </div>

          {tier === "own_domain" && saved.tier === "own_domain" && (saved.dns?.length ?? 0) > 0 && (
            <div className="space-y-2" data-testid="section-email-dns">
              <p className="text-xs font-semibold text-foreground">Add these 3 records at your domain registrar</p>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-left text-xs">
                  <thead className="bg-muted/30 text-muted-foreground">
                    <tr><th className="px-3 py-2 font-medium">Type</th><th className="px-3 py-2 font-medium">Host</th><th className="px-3 py-2 font-medium">Value</th><th className="px-3 py-2 font-medium">Status</th></tr>
                  </thead>
                  <tbody>
                    {saved.dns!.map((r, i) => (
                      <tr key={`${r.host}-${i}`} className="border-t border-border align-top" data-testid={`row-dns-${i}`}>
                        <td className="px-3 py-2 font-mono">{r.type}</td>
                        <td className="px-3 py-2"><div className="break-all font-mono">{r.host}</div><div className="mt-1"><CopyButton value={r.host} label={`host ${i + 1}`} /></div></td>
                        <td className="px-3 py-2"><div className="break-all font-mono">{r.data}</div><div className="mt-1"><CopyButton value={r.data} label={`value ${i + 1}`} /></div></td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.valid ? <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 size={12} /> Found</span> : <span className="inline-flex items-center gap-1 text-amber-400"><Clock size={12} /> Not found yet</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-muted-foreground/70">DNS changes can take a few minutes to a few hours. Press "Check again" when you've added them.</p>
            </div>
          )}

          <div className="space-y-3 rounded-xl border border-border bg-muted/20 p-3 md:p-4" data-testid="section-email-footer">
            <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-emerald-400" /><p className="text-sm font-semibold text-foreground">Footer on every automated email</p></div>
            <p className="text-[11px] text-muted-foreground/80">Canada's anti-spam law (CASL) requires who is sending, where, and a working unsubscribe link. We add the unsubscribe link ourselves.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="email-legal-name">Legal name</label>
                <input id="email-legal-name" className={`${inputCls} mt-1`} value={legalName} onChange={(e) => setLegalName(e.target.value)} disabled={locked || busy} placeholder={data.footerDefaults.legalName || "Acme Accounting Inc."} data-testid="input-email-legal-name" />
              </div>
              <div>
                <label className={labelCls} htmlFor="email-unsub-text">Unsubscribe wording (optional)</label>
                <input id="email-unsub-text" className={`${inputCls} mt-1`} value={unsubText} onChange={(e) => setUnsubText(e.target.value)} disabled={locked || busy} placeholder="To stop these emails, unsubscribe here:" data-testid="input-email-unsubscribe-text" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="email-address">Mailing address</label>
                <textarea id="email-address" rows={2} className={`${inputCls} mt-1 resize-none`} value={address} onChange={(e) => setAddress(e.target.value)} disabled={locked || busy}
                  placeholder={data.footerDefaults.address || "123 King St W, Toronto, ON M5H 1A1, Canada"} data-testid="input-email-address" />
              </div>
            </div>
          </div>

          {formError && <p className="flex items-start gap-1.5 text-xs text-red-300" data-testid="text-email-form-error"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{formError}</p>}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={!dirty || locked || busy}
              title={locked ? (disabledReason ?? "You don't have permission to change this.") : undefined}
              className="flex items-center gap-2 px-4 py-3 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-medium hover:bg-blue-600/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
              data-testid="button-save-email-sender"
            >
              {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save email settings
            </button>
            {saved.tier !== "platform" && (
              <button
                type="button"
                onClick={() => refresh.mutate()}
                disabled={locked || busy}
                className="flex items-center gap-2 px-4 py-3 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted/40 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                data-testid="button-email-check-again"
              >
                {refresh.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Check again
              </button>
            )}
            <button
              type="button"
              onClick={() => test.mutate()}
              disabled={locked || busy || dirty || !isSavedTierReady}
              title={dirty ? "Save your changes first" : !isSavedTierReady ? "Your sender isn't ready yet" : undefined}
              className="flex items-center gap-2 px-4 py-3 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted/40 transition-colors disabled:opacity-50 disabled:pointer-events-none"
              data-testid="button-email-send-test"
            >
              {test.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send me a test email
            </button>
          </div>
          {locked && disabledReason && <p className="text-[11px] text-muted-foreground/60" data-testid="text-email-disabled-reason">{disabledReason}</p>}

          <div className="border-t border-border pt-5 space-y-3" data-testid="section-rogers-send-policy">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Rogers' sending policy</h3>
              <p className="text-[11px] text-muted-foreground/80 mt-0.5" data-testid="text-casl-gate">
                Rogers only sends without your click when the message is a reply to the lead's own inquiry (within 180 days) or the lead gave express consent, your sender is ready, your footer is complete, the email has an unsubscribe link, the lead hasn't opted out and wasn't emailed in the last 24 hours. Anything else waits for your approval.
              </p>
            </div>
            {!policyAvailable && <p className="text-xs text-muted-foreground" data-testid="text-send-policy-unavailable">Sending policies aren't available on this server yet, so every email waits for your approval.</p>}
            <fieldset className="space-y-2" disabled={locked || !policyAvailable || savePolicy.isPending} data-testid="radiogroup-send-policy">
              <legend className="sr-only">How Rogers' emails go out</legend>
              {policyOptions(publishPolicy?.email?.vetoHours ?? DEFAULT_VETO_HOURS).map((o) => {
                const checked = mode === o.value;
                return (
                  <label
                    key={o.value}
                    className={`flex items-start gap-3 rounded-xl border p-3 transition-all ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${checked ? "border-blue-500/40 bg-blue-500/10" : "border-border bg-muted/20 hover:border-border/70"}`}
                    data-testid={`option-send-policy-${o.value}`}
                  >
                    <input type="radio" name="send-policy" value={o.value} checked={checked} onChange={() => setMode(o.value)} className="mt-1 accent-blue-500" data-testid={`radio-send-policy-${o.value}`} />
                    <span className="min-w-0"><span className="block text-sm font-semibold text-foreground">{o.title}</span><span className="block text-[11px] text-muted-foreground/80">{o.desc}</span></span>
                  </label>
                );
              })}
            </fieldset>
            {mode !== "manual" && needsAttention.length > 0 && (
              <p className="flex items-start gap-1.5 text-[11px] text-amber-400" data-testid="text-send-policy-attention">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                <span>Until {needsAttention.join(" and ")}, every email still waits for your approval.</span>
              </p>
            )}
            <button
              type="button"
              onClick={() => savePolicy.mutate()}
              disabled={mode === savedMode || locked || !policyAvailable || savePolicy.isPending}
              title={locked ? (disabledReason ?? "You don't have permission to change this.") : undefined}
              className="flex items-center gap-2 px-4 py-3 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-medium hover:bg-blue-600/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
              data-testid="button-save-send-policy"
            >
              {savePolicy.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save sending policy
            </button>
          </div>
        </>
      )}
    </div>
  );
}
